/**
 * "Variant grouping rule" section of the Shopify / WooCommerce / Generic URL
 * source editors. Groups rows the store leaves standalone, by a key computed
 * from each reference — proposed in the import preview, written only when the
 * curator accepts a group there. See CuratedWatches/variantGroupingRule.ts.
 */
import { useState } from 'react';
import type { VariantGroupingRule } from '../../types';
import { ruleGroupKey, validateRule } from '../CuratedWatches/variantGroupingRule';

interface Props {
  rule: VariantGroupingRule | undefined;
  onChange: (rule: VariantGroupingRule | undefined) => void;
}

const TYPES: Array<{ type: VariantGroupingRule['type']; label: string; help: string; placeholder?: string }> = [
  { type: 'none', label: 'None', help: 'Every row the store leaves standalone imports standalone.' },
  { type: 'stem', label: 'Stem', help: 'Rows sharing their first N characters group together.', placeholder: 'e.g. 7' },
  {
    type: 'splitter',
    label: 'Splitter',
    help: 'Rows sharing everything before the first splitter character group together. Several characters = any of them.',
    placeholder: 'e.g. - or /',
  },
  {
    type: 'regex',
    label: 'Regex',
    help: 'Rows whose pattern match (capture group 1, else the whole match) is the same group together.',
    placeholder: 'e.g. ^([A-Z]+\\d+)',
  },
];

export function VariantGroupingRuleEditor({ rule, onChange }: Props) {
  const current = rule ?? { type: 'none' as const };
  const [sample, setSample] = useState('');
  const def = TYPES.find((t) => t.type === current.type) ?? TYPES[0];
  const error = validateRule(current);
  const key = sample.trim() && !error ? ruleGroupKey(sample, current) : null;

  return (
    <div className="space-y-2 rounded border border-gray-200 p-2">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Variant grouping rule</label>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Variant grouping rule"
            value={current.type}
            onChange={(e) => {
              const type = e.target.value as VariantGroupingRule['type'];
              // `none` clears the field, so an untouched source keeps no rule at all.
              onChange(type === 'none' ? undefined : { type, value: type === current.type ? current.value : '' });
            }}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            {TYPES.map((t) => (
              <option key={t.type} value={t.type}>
                {t.label}
              </option>
            ))}
          </select>
          {current.type !== 'none' && (
            <input
              aria-label="Variant grouping rule value"
              value={current.value ?? ''}
              onChange={(e) => onChange({ type: current.type, value: e.target.value })}
              placeholder={def.placeholder}
              inputMode={current.type === 'stem' ? 'numeric' : undefined}
              className="flex-1 min-w-[8rem] text-sm border border-gray-300 rounded px-2 py-1 font-mono"
            />
          )}
        </div>
        <p className="text-[11px] text-gray-400 mt-1">{def.help}</p>
        {current.type !== 'none' && error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
      </div>
      {current.type !== 'none' && !error && (
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
          <input
            aria-label="Try a reference"
            value={sample}
            onChange={(e) => setSample(e.target.value)}
            placeholder="Try a reference…"
            className="w-40 border border-gray-300 rounded px-2 py-0.5 font-mono"
          />
          {sample.trim() && (key ? <span>→ group <span className="font-mono text-gray-800">{key}</span></span> : <span>→ no group (imports standalone)</span>)}
        </div>
      )}
      <p className="text-[11px] text-gray-400">
        Groups are only proposed: the import preview lists each one to accept, re-parent, split or reject. Rows the
        store already groups keep their store parent.
      </p>
    </div>
  );
}
